// Last-known org branding, so the first paint can use it before the settings request returns.
// Keyed by organization id. Not a source of truth; the network refresh overwrites it.

import type { OrgSettings } from "@/hooks/useOrgSettings";

const STORAGE_PREFIX = "turbo.orgSettings.v1.";
const memoryCache = new Map<string, OrgSettings>();

function persistentStore(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isSettings(value: unknown): value is OrgSettings {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.appDisplayName === "string" && typeof row.brandName === "string" && typeof row.organizationName === "string";
}

export function readOrgSettingsCache(orgId: string | null, store?: Storage | null): OrgSettings | null {
  if (!orgId) return null;
  if (store === undefined) {
    const remembered = memoryCache.get(orgId);
    if (remembered) return remembered;
  }
  const bag = store === undefined ? persistentStore() : store;
  if (!bag) return null;
  try {
    const raw = bag.getItem(`${STORAGE_PREFIX}${orgId}`);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isSettings(parsed)) return null;
    if (store === undefined) memoryCache.set(orgId, parsed);
    return parsed;
  } catch {
    return null;
  }
}

export function writeOrgSettingsCache(orgId: string, settings: OrgSettings, store?: Storage | null): void {
  if (store === undefined) memoryCache.set(orgId, settings);
  const bag = store === undefined ? persistentStore() : store;
  if (!bag) return;
  try {
    bag.setItem(`${STORAGE_PREFIX}${orgId}`, JSON.stringify(settings));
  } catch {
    /* private mode / quota */
  }
}
